import { useEffect, useMemo, useState } from 'react';
import ProductConfigurationIcon from 'assets/icons/product-configuration-icon.svg?react';
import ActivationStatusIcon from 'assets/icons/activation-status-icon.svg?react';
import { selectBrUid } from 'selectors/accountSelector';
import { useDispatch, useSelector } from 'react-redux';
import { selectPartnerId } from 'selectors/partnerStatusSelector';
import RightSideBottomBar from 'components/common/RightSideBottomBar';
import ProductDetailActions from 'components/productDetails/ProductDetailActions';
import ProductConfigurationView from '../../components/productDetails/ProductConfigurationView';
import ProductSummary from 'components/productDetails/ProductSummary';
import ActivationStatus from 'components/productDetails/ActivationStatus';
import TaskOverview from 'components/productDetails/TaskOverview';
import Documents from 'components/productDetails/documents/Documents';
import AccordionGridWrapper from 'components/common/AccordionGridWrapper';
import StickyHeaderWrapper from 'components/common/StickyHeaderWrapper';
import OpenTask from 'components/productDetails/OpenTask';
import ServiceRoles from 'components/productDetails/ServiceRoles';
import { Task } from 'pco-product-service-internal-consumer';
import { getServiceRoleDefinition, getStepMetadata } from 'utils/json-form/formDataUtils';
import { AppDispatch, RootState } from 'store';
import { Box, Button, Grid, Tooltip, Typography } from '@mui/material';
import { LoadingSpinner } from 'components/loadingSpinner/LoadingSpinner';
import CommonDialog from 'components/common/CommonDialog';
import ServiceRoleFormWithPartnerSearch from 'components/productConfiguration/ServiceRoleFormWithPartnerSearch';
import CloseIcon from 'assets/icons/close-icon.svg?react';
import { jbColors } from 'theme/jbColors';
import { ProductDetailsNavigateToTask } from './ProductDetailsNavigateToTask';
import { setOpenedTaskUid } from 'reducers/serviceAgreementsReducer';
import { StepMetadataType } from 'types';

const ProductDetails = () => {
  const [, setIsFormulateDecisionDialogOpen] = useState(false);
  const { selectedServiceAgreement } = useSelector((state: RootState) => state.serviceAgreements);
  const { loader } = useSelector((state: RootState) => state.appConfig);
  const brUid = useSelector(selectBrUid);
  const partnerUid = useSelector(selectPartnerId);
  const customStepMetadata = selectedServiceAgreement
    ? getStepMetadata(selectedServiceAgreement, StepMetadataType.CUSTOM_COMPONENT)
    : undefined;
  const preapprovalStepMetadata = selectedServiceAgreement
    ? getStepMetadata(selectedServiceAgreement, StepMetadataType.PREAPPROVAL_FORM_METADATA)
    : undefined;
  const infoStepMetadata = selectedServiceAgreement
    ? getStepMetadata(selectedServiceAgreement, StepMetadataType.INFORMATION_METADATA)
    : undefined;
  const [additionalDelegateModalOpen, setAdditionalDelegateModalOpen] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  const editProductDetailsUrl = useMemo<string>(() => {
    const processInstanceId = selectedServiceAgreement?.processInstanceId;
    if (brUid) {
      return `/accounts/${brUid}/serviceAgreement/${processInstanceId}/configuration`;
    }
    return `/partner/${partnerUid}/serviceAgreement/${processInstanceId}/configuration`;
  }, [brUid, partnerUid, selectedServiceAgreement?.processInstanceId]);

  const openTask = useMemo<Task | null>(() => {
    const inProgress = selectedServiceAgreement?.tasks?.find((task) => task.resolution === 'In progress');
    return inProgress ?? null;
  }, [selectedServiceAgreement]);

  useEffect(() => {
    if (openTask) {
      dispatch(setOpenedTaskUid(openTask.taskUid));
    }
  }, [openTask, dispatch]);

  const openFormulateDecisionDialog = () => {
    setIsFormulateDecisionDialogOpen(true);
  };

  const stepMetaData = customStepMetadata || preapprovalStepMetadata || infoStepMetadata;

  const serviceRolesWithPendingTasks =
    selectedServiceAgreement?.serviceRoles?.filter((sr) =>
      sr?.stepMetadata?.find((t) => t !== undefined && t.workAccess),
    ) ?? [];

  return (
    <>
      <span data-testid="product-details-view"></span>
      {loader.loading ? (
        <LoadingSpinner message={loader.message} />
      ) : (
        <StickyHeaderWrapper
          hasMultipleTasksOpen={serviceRolesWithPendingTasks?.length > 1}
          hasStickyBottomBar={true}
          side="right"
          hasOpenTask={
            !!(
              openTask ||
              customStepMetadata ||
              (serviceRolesWithPendingTasks.length > 0 && selectedServiceAgreement?.agreementStatus !== 'WITHDRAWN')
            )
          }
          topComponents={
            <AccordionGridWrapper
              expandedProp={true}
              showExpandIcon={false}
              title={selectedServiceAgreement?.serviceDefinitionName ?? ''}
              customCloseAction={true}
            >
              <>
                {/*Service Agreement Task */}
                <Box sx={{ mb: 1 }}>
                  {(openTask || stepMetaData) && (
                    <OpenTask
                      stepMetadata={stepMetaData}
                      taskUid={
                        selectedServiceAgreement?.stepMetadata?.find(
                          (x) => x?.stepMetadataType === StepMetadataType.PREAPPROVAL_FORM_METADATA,
                        )?.taskId
                      }
                    />
                  )}
                </Box>

                <ProductDetailsNavigateToTask />
              </>
            </AccordionGridWrapper>
          }
          centerComponents={
            <>
              {selectedServiceAgreement && (
                <AccordionGridWrapper
                  title={'Activation Status'}
                  titleIcon={<ActivationStatusIcon style={{ marginLeft: '0.4em' }} />}
                >
                  <ActivationStatus serviceAgreement={selectedServiceAgreement} />
                </AccordionGridWrapper>
              )}
              {selectedServiceAgreement && (
                <AccordionGridWrapper
                  titleIcon={<ProductConfigurationIcon style={{ marginLeft: '0.4em' }} />}
                  title={'Product Summary'}
                >
                  <ProductSummary serviceAgreement={selectedServiceAgreement} />
                </AccordionGridWrapper>
              )}
              {selectedServiceAgreement?.tasks?.length !== 0 && selectedServiceAgreement?.tasks && (
                <AccordionGridWrapper
                  title={'Task Overview'}
                  titleIcon={<ProductConfigurationIcon style={{ marginLeft: '0.4em' }} />}
                >
                  <TaskOverview tasks={selectedServiceAgreement?.tasks ?? []} />
                </AccordionGridWrapper>
              )}
              {selectedServiceAgreement?.attributes?.length !== 0 &&
                selectedServiceAgreement?.attributes?.filter((a) =>
                  Object.keys(JSON.parse(a.value ?? '{}')).find((o) => o !== 'serviceRole'),
                )[0] && (
                  <AccordionGridWrapper
                    titleIcon={<ProductConfigurationIcon style={{ marginLeft: '0.4em' }} />}
                    title={'Product Configuration'}
                  >
                    {selectedServiceAgreement.attributes[0]?.value ? (
                      <div style={{ flexDirection: 'row', display: 'flex' }}>
                        <Grid container item xs={6} sx={{ display: 'block' }}>
                          <Typography sx={{ height: '2em' }}>Current Configuration</Typography>
                          <Grid container>
                            <ProductConfigurationView
                              attributes={JSON.parse(selectedServiceAgreement.attributes[0]?.value)}
                              title={''}
                            />
                          </Grid>
                        </Grid>
                        {selectedServiceAgreement.agreementStatus === 'IN_MODIFICATION' && (
                          <Grid container item xs={6} sx={{ display: 'block' }}>
                            <Typography sx={{ height: '2em' }}>Proposed Configuration</Typography>
                            {selectedServiceAgreement?.modificationList?.filter(
                              (m) => m.status === 'IN_PROGRESS',
                            )[0] && (
                              <Grid container>
                                <ProductConfigurationView
                                  attributes={JSON.parse(
                                    selectedServiceAgreement?.modificationList?.filter(
                                      (m) => m.status === 'IN_PROGRESS',
                                    )[0]?.payload || '{}',
                                  )}
                                  title={''}
                                />
                              </Grid>
                            )}
                          </Grid>
                        )}
                      </div>
                    ) : (
                      <Typography>Please configure product.</Typography>
                    )}
                  </AccordionGridWrapper>
                )}
              {(selectedServiceAgreement?.serviceDefinitionName === 'ID Delegation' ||
                (selectedServiceAgreement?.serviceRoles && selectedServiceAgreement?.serviceRoles.length !== 0)) && (
                <AccordionGridWrapper title={'Service Role Overview'}>
                  <>
                    <ServiceRoles serviceRoles={selectedServiceAgreement?.serviceRoles ?? []} />
                    {selectedServiceAgreement?.serviceDefinitionName === 'ID Delegation' && (
                      <Tooltip
                        title={
                          getServiceRoleDefinition(selectedServiceAgreement, 'ID Delegate')?.creationDetails?.message
                        }
                      >
                        <span>
                          <Button
                            disabled={
                              !getServiceRoleDefinition(selectedServiceAgreement, 'ID Delegate')?.creationDetails
                                ?.canBeAdded || selectedServiceAgreement.agreementStatus === 'WITHDRAWN'
                            }
                            sx={{
                              mt: 2,
                              backgroundColor: 'white',
                              ':hover': {
                                backgroundColor: jbColors.core.reflexBlue[100],
                                color: 'white',
                              },
                            }}
                            onClick={() => setAdditionalDelegateModalOpen(true)}
                          >
                            Add Additional Delegate
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </>
                </AccordionGridWrapper>
              )}

              <CommonDialog
                isOpen={additionalDelegateModalOpen}
                onClose={() => {
                  setAdditionalDelegateModalOpen(false);
                }}
                title={'Add Additional Delegate'}
                closeIcon={<CloseIcon onClick={() => setAdditionalDelegateModalOpen(false)} />}
                removeCancel={true}
                content={
                  <ServiceRoleFormWithPartnerSearch setAdditionalDelegateModalOpen={setAdditionalDelegateModalOpen} />
                }
              />

              {selectedServiceAgreement?.documents?.length !== 0 && selectedServiceAgreement?.documents && (
                <AccordionGridWrapper title={'Documents'}>
                  <Documents documents={selectedServiceAgreement?.documents ?? []} />
                </AccordionGridWrapper>
              )}
            </>
          }
        />
      )}

      {!loader.loading && (
        <RightSideBottomBar>
          <ProductDetailActions
            partnerUid={partnerUid}
            brUid={brUid}
            navigationUrl={editProductDetailsUrl}
            openTask={openFormulateDecisionDialog}
          />
        </RightSideBottomBar>
      )}
    </>
  );
};

export default ProductDetails;
